package com.tovtalk.presentation.viewmodel.chat

import com.tovtalk.presentation.ViewModel
import com.tovtalk.domain.entity.ChatThread
import com.tovtalk.domain.repository.ChatRepository
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

/**
 * Chat List ViewModel - רשימת שיחות
 */
class ChatListViewModel(
    private val chatRepository: ChatRepository,
    private val scope: CoroutineScope
) : ViewModel() {
    
    private val _chatThreads = MutableStateFlow<UiState<List<ChatThread>>>(UiState.Loading)
    val chatThreads: StateFlow<UiState<List<ChatThread>>> = _chatThreads.asStateFlow()

    private val _chatRequests = MutableStateFlow<UiState<List<ChatThread>>>(UiState.Loading)
    val chatRequests: StateFlow<UiState<List<ChatThread>>> = _chatRequests.asStateFlow()

    private var chatThreadsJob: Job? = null
    private var chatRequestsJob: Job? = null
    
    init {
        loadChatThreads()
        loadChatRequests()
    }
    
    private fun loadChatThreads() {
        chatThreadsJob?.cancel()
        chatThreadsJob = scope.launch {
            chatRepository.getChatThreads()
                .onStart { _chatThreads.value = UiState.Loading }
                .catch { e -> _chatThreads.value = UiState.Error(e.message ?: "Failed to load chats") }
                .collect { threads ->
                    _chatThreads.value = UiState.Success(threads)
                }
        }
    }
    
    private fun loadChatRequests() {
        chatRequestsJob?.cancel()
        chatRequestsJob = scope.launch {
            chatRepository.getChatRequests()
                .onStart { _chatRequests.value = UiState.Loading }
                .catch { e -> _chatRequests.value = UiState.Error(e.message ?: "Failed to load requests") }
                .collect { threads ->
                    _chatRequests.value = UiState.Success(threads)
                }
        }
    }

    /**
     * Call when leaving the chat list screen (e.g. from DisposableEffect.onDispose).
     * Stops Firestore listeners so they don't keep running in the background.
     */
    fun onClear() {
        chatThreadsJob?.cancel()
        chatThreadsJob = null
        chatRequestsJob?.cancel()
        chatRequestsJob = null
    }
    
    fun acceptRequest(thread: ChatThread) {
        scope.launch {
            chatRepository.acceptChatRequest(thread.otherUser.id.toString())
        }
    }
    
    fun declineRequest(thread: ChatThread) {
        scope.launch {
            chatRepository.declineChatRequest(thread.otherUser.id.toString())
        }
    }
}
